import ko from "knockout";
import _ from "lodash";
import navHelper from "services/navHelper";
import ViewModel from "viewModels/ViewModel";
import NavMenu from "viewModels/NavMenu";
import SwitchedView from "viewModels/SwitchedView";
import { getHashRoute } from "util/uriUtil";

/**
 * A ViewModel to help with the management of displaying a set of tabs and their
 * respective content.
 *
 * @param {array} tabMenuItems - an array of objects that must adhere to following
 * structure:
 *
 *     {
 *       hash: "#/route"     // A string representing the route that the browser
 *                           // should be set to when this item is selected
 *
 *       label: "messageKey" // A message key whose value is the text that will
 *                           // display for the nav button
 *
 *
 *
 *       cssClass: "string"    // (optional) A string with a space-separated list of css class names to apply.
 *     }
 *
 * @param {map} tabViewsMap  a map of view keys and some metadata to determine which
 *                           ViewModel and template should displayed for a tab.
 *
 *     {
 *       'viewKey': {
 *          view: ViewModelConstructor // A reference to the ViewModel's constructor that should be used for active tab's data
 *          template: "string"         // The path to the template that should be used as the content for the active tab
 *          persist: boolean           // (optional) Defaults to false. Indicates whether the tab view should be persistent.
 *          persistDOM: boolean // (optional) Indicates wehther the tab view should be constructed by the TabbedContentView (implies persist:true)
 *          arguments: Array           // (optional) Array of arguments to pass to the ViewModel constructor
 *       }
 *     }
 */
const TabbedContentView = ViewModel.extend(function (
    tabMenuItems,
    tabViewsMap
) {
    const self = this;

    //
    // Private Variables
    //
    const subViews = self.subView(SwitchedView);
    const subViewsMap = ko.observable({}).extend({ deferred: true });

    // ===============================================================================
    // Navigation
    // ===============================================================================

    self.navMenu = self.subView(NavMenu);

    // ===============================================================================
    // View Management
    // ===============================================================================
    /**
     * tab views constructed with a truthy `persistDOM` value
     *
     * @type {array}
     */
    self.persistDOMTabViews = self.computed(function () {
        return _.filter(subViewsMap(), "persistDOM");
    });

    self.currentViewData = self.computed(function () {
        const viewKey = subViews.getActiveView();
        const activeView = subViewsMap()[viewKey];
        const isPersistDOM = activeView && activeView.persistDOM;
        const tabId = activeView && activeView.tabId;
        const view = isPersistDOM ? activeView : ko.unwrap(subViews[viewKey]);

        if (!view) {
            return null;
        }

        return {
            view: view,
            viewKey: viewKey,
            template: activeView.template,
            isPersistDOM: !!isPersistDOM,
            tabId: tabId,
            classNames: activeView?.classNames || {},
        };
    });

    self.containsTab = function (tabKey) {
        return !_.isUndefined(subViewsMap()[tabKey]);
    };

    // Returns true if the subView with given tabKey and navItem with given hash both exist.
    self.containsTabAndHash = function (tabKey, hash) {
        const viewToRemove = subViewsMap()[tabKey];
        if (viewToRemove) {
            return self.navMenu.containsNavItem({ hash: hash });
        }
        return false;
    };

    self.getSubViewById = function (tabKey) {
        if (subViews[tabKey]) {
            return subViews[tabKey]();
        }
    };

    self.addTabViews = function (tabMenuItems, tabViewsMap) {
        const newSubViewsMap = subViewsMap();

        _.each(tabMenuItems, function (tabMenuItem) {
            const { hash } = tabMenuItem;
            let { href } = tabMenuItem;
            if (hash && !href) {
                href = navHelper.asHostUrl(hash);
                Object.assign(tabMenuItem, {
                    handleNavItemClick(bindingContext, event) {
                        event.preventDefault();
                        navHelper.navigateTo(href);
                    },
                });
            }
            self.navMenu.addNavItem(tabMenuItem);
        });

        _.forIn(tabViewsMap, function (value, key) {
            newSubViewsMap[key] = _.assign({}, value);
            newSubViewsMap[key].viewKey = key;

            const args = Array.prototype.concat.call(
                [
                    key,
                    // SwitchedView will skip construction if we pass no constructor
                    value.persistDOM ? undefined : value.view,
                ],
                value.arguments
            );

            if (value.persistDOM) {
                newSubViewsMap[key].view = self.subView.apply(
                    self,
                    [value.view].concat(value.arguments)
                );
            }

            if (value.persist || value.persistDOM) {
                subViews.addPersistentView.apply(subViews, args);
            } else {
                subViews.addView.apply(subViews, args);
            }
        });

        subViewsMap(newSubViewsMap);
    };

    self.removeTabViews = function () {
        subViewsMap({});
    };

    // Removes the navItem and subview associated with the tabKey and hash.
    // If both are not present, neither will be removed. If this causes the
    // active view to be removed, it is up to the caller to activate a different
    // view.
    self.removeTabView = function (tabKey, hash) {
        if (self.containsTabAndHash(tabKey, hash)) {
            self.navMenu.removeNavItem({ hash: hash });
            subViews.invalidateViewInstance(tabKey);
            const newSubViewsMap = subViewsMap();
            delete newSubViewsMap[tabKey];
            subViewsMap(newSubViewsMap);
        }
    };

    self.removeActiveTabView = function () {
        const hostHash = navHelper.asHostHash(getHashRoute());
        self.navMenu.removeNavItem({ hash: hostHash });
        const activeView = subViews.getActiveView();
        subViews.disposeView(activeView);
        const newSubViewsMap = subViewsMap();
        delete newSubViewsMap[activeView];
        subViewsMap(newSubViewsMap);
        self.activateDefaultTabView();
    };

    self.activateDefaultTabView = function () {
        // Activate the first tab
        const navItems = self.navMenu.navItems();
        _.forIn(navItems, function (value) {
            if (getHashRoute() !== value.localHash) {
                navHelper.navigateTo(value.hash);
            }
            return false;
        });
    };

    self.setActiveTabView = function (/*tabKey, args*/) {
        self.navMenu.setActive(getHashRoute());
        return subViews.setActiveView.apply(subViews, arguments);
    };

    // ===============================================================================
    // Initialize
    // ===============================================================================

    self.addTabViews(tabMenuItems, tabViewsMap);
});

export default TabbedContentView;
